import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { GROUPS } from '../mockData.js';
import {
  ACTION_NAMES, ADDRESSES, PRODUCTS, PREVIEW_MIN_DATE, describePlanValue, displayDate,
  draftError, draftTerms, getDraft, isDraftDirty, termsKey,
  type AddressKey, type ProductKey, type SubscriptionDialogAction,
  type SubscriptionInput, type SubscriptionReviewState,
} from '../subscriptionState.js';

export interface SubscriptionDialogProps {
  open: boolean;
  action: SubscriptionDialogAction | null;
  state: SubscriptionReviewState;
  busy: boolean;
  onClose: () => void;
  onInput: (input: SubscriptionInput) => void;
  onAgree: (agreed: boolean) => void;
  onSave: (returnAfter: boolean) => void;
  onDiscard: () => void;
  onCheck: () => void;
  onPaymentPreview: () => void;
}

const money = (amount: number): string => Number.isFinite(amount)
  ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'Check quantities';

// Preservation contract (R8 editor-state scope #22):
//   - This dialog is #22 (subscription editor). The payment flow in
//     BuildOrderReview (#4) owns its own PaymentDialog instance and
//     never shares state with this one.
//   - When `action === 'payment'`, this dialog renders only a provider
//     handoff; it does not post card data and does not mutate plan
//     drafts. That branch is a scoped preview surface for the
//     secure-payment handoff and nothing else.
export function SubscriptionDialog(props: SubscriptionDialogProps): ReactNode {
  const { open, action, state, busy, onClose, onInput, onAgree, onSave, onDiscard, onCheck, onPaymentPreview } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);
  const plan = state.plans[state.selected];
  const payment = action === 'payment';
  const draft = action && action !== 'payment' ? getDraft(state, state.selected, action) : null;
  const input = draft?.input || {};
  const terms = draft ? draftTerms(state, draft) : null;
  const pending = Boolean(state.pending);
  const locked = busy || pending;
  const validation = draft ? draftError(state, draft, false) : '';
  const saveError = draft ? draftError(state, draft) : '';
  const agreed = Boolean(draft && draft.agreedTerms === termsKey(state, draft));
  const setField = <K extends keyof SubscriptionInput,>(key: K, value: SubscriptionInput[K]): void => onInput({ ...input, [key]: value });

  const dateField = (label: string): ReactNode => (
    <label>{label}<input type="date" min={PREVIEW_MIN_DATE} value={input.date || ''} disabled={locked}
      onChange={event => setField('date', event.target.value)} /></label>
  );
  const noteField = (): ReactNode => (
    <label>Note · Optional<textarea rows={2} value={input.note || ''} placeholder="What Sarah shared"
      disabled={locked} onChange={event => setField('note', event.target.value)} /></label>
  );
  const reasonField = (cancellation = false): ReactNode => (
    <label>Customer’s reason<select value={input.reason || ''} disabled={locked}
      onChange={event => setField('reason', event.target.value)}>
      <option value="">Choose Sarah’s reason</option>
      {cancellation ? <>
        <option value="no_longer_needed">No longer needs this subscription</option>
        <option value="customer_request">Customer requested cancellation</option>
      </> : <>
        <option value="more_supply">Still has enough product</option>
        <option value="customer_schedule">Customer’s schedule changed</option>
      </>}
      <option value="other">Other reason</option>
    </select></label>
  );
  const products = input.products || [];
  const editProduct = (index: number, key: 'product' | 'quantity', value: string): void => {
    setField('products', products.map((product, i) => i === index
      ? { ...product, [key]: value } as { product: ProductKey; quantity: string } : product));
  };

  return (
    <dialog id="subscription-dialog" aria-labelledby="subscription-dialog-title"
      aria-describedby="se-editor-scope" ref={dialogRef}
      onCancel={event => { event.preventDefault(); onClose(); }}>
      <div className="dialog-head">
        <h2 id="subscription-dialog-title">{action ? ACTION_NAMES[action] : 'Subscription change'} · {plan.name}</h2>
        <button type="button" aria-label="Close editor" onClick={onClose}>&times;</button>
      </div>
      <form className="dialog-body se-editor" onSubmit={event => { event.preventDefault(); if (draft && !locked && !saveError) onSave(false); }}>
        <p id="se-editor-scope" className="se-editor-scope">Sarah Mitchell · {plan.name} · Sample subscription</p>
        {payment ? <>
          <p>Use the payment provider’s secure flow. Sarah’s full card details stay with the provider.</p>
          <div className="se-secure-payment">
            <strong>{GROUPS[state.selected].cardBrand === 'VISA' ? 'Visa' : 'Mastercard'} ending in {GROUPS[state.selected].cardLast4}</strong>
            <button className="blue-action" type="button" onClick={onPaymentPreview}>Open secure payment update</button>
          </div>
          <p className="se-example-terms">This preview opens no payment page and sends no link.</p>
        </> : <>
          {action === 'date' && <>
            <div className="se-field-grid">{dateField('Requested next order date')}{reasonField()}</div>
            {noteField()}<p className="se-small-note">Order-processing date, not a guaranteed arrival date. America/Chicago in this example.</p>
          </>}
          {action === 'frequency' && <>
            <div className="se-field-grid">
              <label>Delivery interval<select value={input.cadence || ''} disabled={locked}
                onChange={event => setField('cadence', event.target.value)}>
                {[30, 60, 90].map(days => <option key={days} value={String(days)}>Every {days} days</option>)}
              </select></label>
              {dateField('Next order processes')}
            </div>
            <p className="se-small-note">Available intervals and the next order date come from the verified plan. These options are illustrative.</p>
          </>}
          {action === 'products' && <div className="se-product-edit">
            {products.map((product, index) => <div className="se-product-row" key={index}>
              <label>Product {index + 1}<select value={product.product} disabled={locked}
                onChange={event => editProduct(index, 'product', event.target.value)}>
                {(Object.keys(PRODUCTS) as ProductKey[]).map(key => <option key={key} value={key}>{PRODUCTS[key].name} · 1 gallon</option>)}
              </select></label>
              <label>Quantity {index + 1}<input type="number" min="1" step="1" value={product.quantity} disabled={locked}
                onChange={event => editProduct(index, 'quantity', event.target.value)} /></label>
              <button className="text-btn" type="button" disabled={locked} aria-label={'Remove product ' + (index + 1)}
                onClick={() => setField('products', products.filter((_, i) => i !== index))}>Remove</button>
            </div>)}
            <button className="outlined" type="button" disabled={locked || products.length >= Object.keys(PRODUCTS).length}
              onClick={() => { const key = (Object.keys(PRODUCTS) as ProductKey[]).find(k => !products.some(p => p.product === k)); if (key) setField('products', [...products, { product: key, quantity: '1' }]); }}>+ Add product</button>
          </div>}
          {action === 'address' && <>
            <label>Saved shipping address<select value={input.address || ''} disabled={locked}
              onChange={event => setField('address', event.target.value as AddressKey)}>
              {(Object.keys(ADDRESSES) as AddressKey[]).map(key => <option key={key} value={key}>{ADDRESSES[key]}</option>)}
            </select></label>
            <p className="se-small-note">This changes the selected subscription’s destination. Other orders and the customer default stay separate.</p>
          </>}
          {action === 'skip' && <p>Skip the selected plan’s upcoming order on <strong>{displayDate(plan.nextDate)}</strong>. The following order is shown below; the delivery interval stays the same.</p>}
          {action === 'pause' && <>{dateField('Requested resume date')}{noteField()}<p className="se-small-note">Confirm the pause timing and resume date with Sarah.</p></>}
          {(action === 'resume' || action === 'reactivate') && <>{dateField('Requested next order date')}<p className="se-small-note">Confirm the resumed plan and next order with Sarah.</p></>}
          {action === 'cancel' && <>{reasonField(true)}{noteField()}<p>Cancel <strong>{plan.name}</strong> only. A cancellation does not cancel an order that is already processing.</p></>}
          {terms && action && <section className="se-terms" aria-label="Current and requested subscription details">
            <div className="se-terms-head"><strong>{plan.name} only</strong></div>
            <div className="se-comparison">
              <div><span>Current</span><strong>{describePlanValue(terms.before, action)}</strong></div>
              <span aria-hidden="true">→</span>
              <div><span>Requested</span><strong>{describePlanValue(terms.after, action)}</strong></div>
            </div>
            <dl><dt>Next order processes</dt><dd>{action === 'cancel' ? 'No new recurring order' : displayDate(terms.after.nextDate)}</dd>
              <dt>Recurring merchandise</dt><dd>{money(terms.merchandiseAfter)}{terms.merchandiseBefore !== terms.merchandiseAfter && <small>Previously {money(terms.merchandiseBefore)}</small>}</dd></dl>
            <p>Other subscriptions keep their current settings. Shipping and tax are reviewed with the order.</p>
            {action === 'cancel' && <p>Already-processing orders are reviewed separately.</p>}
            <p className="se-example-terms">Example dates and quote for this design review.</p>
          </section>}
          {!pending && <label className="se-agreement"><input type="checkbox" checked={agreed} disabled={busy || Boolean(validation)}
            onChange={event => onAgree(event.target.checked)} /><span>Sarah agreed to these details.</span></label>}
          <p className="se-small-note" id="se-dialog-hint">{pending ? 'Check the existing save result before another change.' : validation || 'Confirm the details with Sarah, then save.'}</p>
        </>}
        {state.message && <p className={'se-feedback' + (state.error ? ' se-error' : '')} role={state.error ? 'alert' : 'status'}>{state.message}</p>}
        <div className="se-dialog-actions">
          {draft && isDraftDirty(draft) && <button className="text-btn" type="button" disabled={locked} onClick={onDiscard}>Discard edits</button>}
          {pending && <button className="outlined" type="button" disabled={busy} onClick={onCheck}>{busy ? 'Checking save…' : 'Check save result'}</button>}
          <button className="outlined" type="button" onClick={onClose}>Close</button>
          {!payment && <>
            <button className="outlined" type="button" disabled={locked || Boolean(saveError)} onClick={() => onSave(true)}>Save &amp; Return</button>
            <button className="blue-action" type="submit" disabled={locked || Boolean(saveError)}>{busy ? 'Saving…' : 'Save changes'}</button>
          </>}
        </div>
      </form>
    </dialog>
  );
}
